import { useMutation } from "@apollo/client/react"
import Button from "@mui/material/Button"
import Checkbox from "@mui/material/Checkbox"
import FormControlLabel from "@mui/material/FormControlLabel"
import Link from "@mui/material/Link"
import MenuItem from "@mui/material/MenuItem"
import { Formik } from "formik"
import React, { useState } from "react"

import AuthLayout from "@/components/auth/auth_layout"
import { signUpSchema } from "@/components/auth/validation_schemas"
import { TextField } from "@/components/form"
import { CreateAccountDocument, Country } from "@/graphql/graphql"
import i18n from "@/i18n"
import { showErrorToast } from "@/shared/toast"

const STYLES = {
  heading: "mb-8 mt-0 font-semibold text-4xl",
  thankYouContainer: "text-center",
  thankYouLine1: "text-xl font-semibold mb-4",
  thankYouLine2: "text-base text-gray-600",
  form: "w-full",
  fieldsContainer: "flex flex-col gap-4",
  fieldRow: "flex gap-4",
  sectionHeading: "font-semibold mb-2 text-lg mt-2",
  errorText: "mt-4 text-sm text-red-600",
  checkboxContainer: "mt-4",
  checkboxLabel: "text-sm",
  submitButton: "mt-4",
  linkContainer: "mt-4 text-center",
  link: "text-sm",
  card: "flex flex-col justify-center items-center p-12 rounded-lg bg-white shadow-md max-w-2xl w-full",
  container: "flex min-h-screen h-max w-screen bg-gray-200",
} as const

type FormValues = {
  firstName: string
  lastName: string
  phone: string
  email: string
  password: string
  passwordConfirmation: string
  businessName: string
  country: string
  acceptedTerms: boolean
}

const SignUpPage = () => {
  const [error, setError] = useState("")
  const [showThankYou, setShowThankYou] = useState(false)
  const [createAccount] = useMutation(CreateAccountDocument)

  const initialValues: FormValues = {
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    password: "",
    passwordConfirmation: "",
    businessName: "",
    country: "POLAND",
    acceptedTerms: false,
  }

  const onSubmit = async (values: FormValues, { setSubmitting, setFieldError }) => {
    setError("")

    try {
      const { data } = await createAccount({
        variables: {
          firstName: values.firstName,
          lastName: values.lastName,
          phone: values.phone,
          email: values.email,
          password: values.password,
          businessName: values.businessName,
          country: values.country as Country,
        },
      })

      if (data?.createAccount?.success) {
        setShowThankYou(true)
      } else if (data?.createAccount?.errors?.length > 0) {
        const errors = data.createAccount.errors
        let hasFieldErrors = false

        errors.forEach((error) => {
          if (error.path && error.path.length > 0) {
            const fieldName = error.path[0]
            setFieldError(fieldName, error.message)
            hasFieldErrors = true
          }
        })

        if (!hasFieldErrors) {
          const firstError = errors[0]
          setError(firstError.message || i18n.t("app.auth.sign_up.validation_error"))
        }
      } else {
        setError(i18n.t("app.auth.sign_up.generic_error"))
      }
    } catch (err) {
      showErrorToast()
      console.error(err)
      setError(i18n.t("app.auth.sign_up.generic_error"))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthLayout cardClassName={STYLES.card} containerClassName={STYLES.container} testIdPrefix="auth-signup">
      {showThankYou ? (
        <div className={STYLES.thankYouContainer} data-testid="auth-signup-success-message">
          <div className={STYLES.thankYouLine1}>{i18n.t("app.auth.sign_up.thank_you_line1")}</div>
          <div className={STYLES.thankYouLine2}>{i18n.t("app.auth.sign_up.thank_you_line2")}</div>
        </div>
      ) : (
        <>
          <h4 className={STYLES.heading}>{i18n.t("app.auth.sign_up.header")}</h4>

          <Formik
            initialValues={initialValues}
            validationSchema={signUpSchema}
            onSubmit={onSubmit}
            validateAfterSubmit={true}
          >
            {({ values, setFieldValue, isSubmitting, handleSubmit }) => {
              const isFormValid =
                values.firstName &&
                values.lastName &&
                values.phone &&
                values.email &&
                values.password &&
                values.passwordConfirmation &&
                values.businessName &&
                values.country &&
                values.acceptedTerms

              return (
                <form onSubmit={handleSubmit} className={STYLES.form} data-testid="auth-signup-form">
                  <div className={STYLES.fieldsContainer}>
                    <div className={STYLES.fieldRow}>
                      <TextField
                        name="firstName"
                        label={i18n.t("app.auth.sign_up.first_name")}
                        data-testid="auth-signup-first-name-input"
                      />
                      <TextField
                        name="lastName"
                        label={i18n.t("app.auth.sign_up.last_name")}
                        data-testid="auth-signup-last-name-input"
                      />
                    </div>

                    <TextField
                      name="phone"
                      label={i18n.t("app.auth.sign_up.phone")}
                      type="tel"
                      data-testid="auth-signup-phone-input"
                    />

                    <TextField
                      name="email"
                      label={i18n.t("app.auth.sign_up.email")}
                      type="email"
                      data-testid="auth-signup-email-input"
                    />

                    <div className={STYLES.fieldRow}>
                      <TextField
                        name="password"
                        label={i18n.t("app.auth.sign_up.password")}
                        type="password"
                        data-testid="auth-signup-password-input"
                      />
                      <TextField
                        name="passwordConfirmation"
                        label={i18n.t("app.auth.sign_up.password_confirmation")}
                        type="password"
                        data-testid="auth-signup-password-confirmation-input"
                      />
                    </div>

                    <div className={STYLES.sectionHeading}>{i18n.t("app.auth.sign_up.business_information")}</div>

                    <TextField
                      name="businessName"
                      label={i18n.t("app.auth.sign_up.business_name")}
                      data-testid="auth-signup-business-name-input"
                    />

                    <TextField
                      name="country"
                      label={i18n.t("app.auth.sign_up.country")}
                      select
                      data-testid="auth-signup-country-select"
                    >
                      <MenuItem value="POLAND">{i18n.t("app.auth.sign_up.countries.poland")}</MenuItem>
                    </TextField>
                  </div>

                  {!!error && (
                    <div className={STYLES.errorText} data-testid="auth-signup-error-message">
                      {error}
                    </div>
                  )}

                  <div className={STYLES.checkboxContainer}>
                    <FormControlLabel
                      data-testid="auth-signup-terms-checkbox"
                      control={
                        <Checkbox
                          checked={values.acceptedTerms}
                          onChange={(e) => setFieldValue("acceptedTerms", e.target.checked)}
                        />
                      }
                      label={
                        <span className={STYLES.checkboxLabel}>
                          {i18n.t("app.auth.sign_up.accept_terms")}{" "}
                          <Link href="/terms" underline="hover" data-testid="auth-signup-form-terms-link">
                            {i18n.t("app.auth.sign_up.terms_and_conditions")}
                          </Link>
                        </span>
                      }
                    />
                  </div>

                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    fullWidth
                    disabled={isSubmitting || !isFormValid}
                    className={STYLES.submitButton}
                    data-testid="auth-signup-submit-btn"
                  >
                    {isSubmitting ? "..." : i18n.t("app.auth.sign_up.submit")}
                  </Button>

                  <div className={STYLES.linkContainer}>
                    <Link
                      href="/sign_in"
                      variant="body2"
                      underline="hover"
                      className={STYLES.link}
                      data-testid="auth-signup-signin-link"
                    >
                      {i18n.t("app.auth.sign_up.have_account")}
                    </Link>
                  </div>
                </form>
              )
            }}
          </Formik>
        </>
      )}
    </AuthLayout>
  )
}

export default SignUpPage
